import { NextPageContext } from "next";

import getSearchResults from "api/requests/getSearchResults";
import { Article } from "custom-types/Article";
import { Brand } from "custom-types/Brand";
import { Coordinates } from "custom-types/Coordinates";
import { Dispensary } from "custom-types/Dispensary";
import { MenuItem } from "custom-types/MenuItem";
import { SearchPageTypes } from "custom-types/Search";
import { Strain } from "custom-types/Strain";
import { AppStore } from "redux/store";

export const MAX_DISTANCE = 30; // 30 miles
export const DEFAULT_SEARCH_TAKE = 12;
export const DEFAULT_SEARCH_SKIP = 0;

export type SearchState = {
  loading: boolean;
  article: Article[];
  brand: Brand[];
  dispensary: Dispensary[];
  menuItem: MenuItem[];
  strain: Strain[];
  menuItemCategories: string[];
  menuItemDispensaries: Dispensary[];
  categoryRelevanceOrder: SearchPageTypes[];
};

export type SearchResults = Omit<SearchState, "loading">;

export const initialState: SearchState = {
  article: [],
  brand: [],
  categoryRelevanceOrder: [
    "strain",
    "menuItem",
    "dispensary",
    "brand",
    "article",
  ],
  dispensary: [],
  loading: true,
  menuItem: [],
  menuItemCategories: [],
  menuItemDispensaries: [],
  strain: [],
};

export const SEARCH_LOADING = "SEARCH_LOADING";
export const SEARCH_RESULTS = "SEARCH_RESULTS";
export const SEARCH_LOAD_MORE = "SEARCH_LOAD_MORE";

export type SearchLoadingAction = {
  type: typeof SEARCH_LOADING;
};

export type SearchResultsAction = {
  type: typeof SEARCH_RESULTS;
  results: SearchResults;
};

export type SearchLoadMoreAction = {
  type: typeof SEARCH_LOAD_MORE;
  result: Partial<SearchResults>;
};

export type SearchActions =
  | SearchLoadingAction
  | SearchResultsAction
  | SearchLoadMoreAction;

export const getSearchState = async (
  ctx: NextPageContext<AppStore>,
  coordinates: Coordinates,
  countryCode: string,
  excludeType?: string[],
  menuItemMaxDistanceTreatment?: string
) => {
  const {
    query: { q },
    store: { dispatch },
  } = ctx;

  // * Experiment for adding a max distance to the menu items piece of global search.
  // * https://leafly.atlassian.net/browse/SHH-438
  const menuItemMaxDistanceFilter =
    menuItemMaxDistanceTreatment && menuItemMaxDistanceTreatment !== "off"
      ? { "filter[menu_items_max_distance]": menuItemMaxDistanceTreatment }
      : {};

  const results = await getSearchResults(
    {
      ...(excludeType && { "filter[exclude_type]": excludeType }),
      ...menuItemMaxDistanceFilter,
      q,
    },
    coordinates,
    MAX_DISTANCE,
    countryCode,
    DEFAULT_SEARCH_TAKE,
    DEFAULT_SEARCH_SKIP,
    "",
    true
  );

  dispatch<SearchResultsAction>({
    results,
    type: SEARCH_RESULTS,
  });
};

export const searchReducer = (
  state = initialState,
  action: SearchActions
): SearchState => {
  switch (action.type) {
    case SEARCH_LOADING:
      return {
        ...state,
        loading: true,
      };
    case SEARCH_RESULTS:
      return {
        ...state,
        loading: false,
        ...action.results,
      };
    case SEARCH_LOAD_MORE:
      return {
        ...state,
        ...action.result,
      };
    default:
      return state;
  }
};

export default searchReducer;
